import { Fragment, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ExternalLink, ChevronDown, ChevronRight, Layers } from "lucide-react";
import { truncate } from "@/lib/utils";
import type { ValidationIssue } from "@/types/validation";
import { BulkResolveDialog } from "./BulkResolveDialog";

export interface IssueGroup {
  key: string;
  message: string;
  source: string;
  level: ValidationIssue["level"];
  issues: ValidationIssue[];
  unresolvedCount: number;
}

function groupIssuesByMessageAndSource(
  issues: ValidationIssue[],
): IssueGroup[] {
  const map = new Map<string, IssueGroup>();
  for (const issue of issues) {
    const key = `${issue.message}|${issue.source}|${issue.level}`;
    const existing = map.get(key);
    if (existing) {
      existing.issues.push(issue);
      if (issue.status === "unresolved") {
        existing.unresolvedCount += 1;
      }
    } else {
      map.set(key, {
        key,
        message: issue.message,
        source: issue.source,
        level: issue.level,
        issues: [issue],
        unresolvedCount: issue.status === "unresolved" ? 1 : 0,
      });
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => b.unresolvedCount - a.unresolvedCount,
  );
}

interface GroupedIssuesTableProps {
  issues: ValidationIssue[];
  isLoading?: boolean;
  onResolve: (issue: ValidationIssue) => void;
  onViewDetails: (issue: ValidationIssue) => void;
  onScrapeUrl: (url: string) => void;
  /**
   * Optional controlled state for expanded groups
   * If provided, the component will use this instead of internal state
   */
  expandedGroups?: Set<string>;
  onExpandedGroupsChange?: (groups: Set<string>) => void;
}

export function GroupedIssuesTable({
  issues,
  isLoading,
  onResolve,
  onViewDetails,
  onScrapeUrl,
  expandedGroups: controlledExpandedGroups,
  onExpandedGroupsChange,
}: GroupedIssuesTableProps) {
  // Internal state (used only if controlled props not provided)
  const [internalExpandedGroups, setInternalExpandedGroups] = useState<
    Set<string>
  >(new Set());
  const [bulkResolveGroup, setBulkResolveGroup] = useState<IssueGroup | null>(
    null,
  );

  // Use controlled state if provided, otherwise use internal state
  const expandedGroups = controlledExpandedGroups ?? internalExpandedGroups;
  const setExpandedGroups = onExpandedGroupsChange ?? setInternalExpandedGroups;

  const groups = groupIssuesByMessageAndSource(issues);

  const toggleGroup = (key: string) => {
    const next = new Set(expandedGroups);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setExpandedGroups(next);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-12 border border-border rounded-lg">
        <p className="text-muted-foreground">No validation issues found</p>
        <p className="text-sm text-muted-foreground mt-2">
          Try adjusting your filters
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-20">Level</TableHead>
              <TableHead className="w-24">Source</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-20 text-center">Total</TableHead>
              <TableHead className="w-24 text-center">Unresolved</TableHead>
              <TableHead className="w-40 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => {
              const expanded = expandedGroups.has(group.key);
              return (
                <Fragment key={group.key}>
                  <TableRow className="cursor-pointer">
                    <TableCell onClick={() => toggleGroup(group.key)}>
                      {expanded ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          group.level === "error" ? "destructive" : "secondary"
                        }
                      >
                        {group.level}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="uppercase text-xs">
                        {group.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-foreground">
                      {truncate(group.message, expanded ? 1000 : 60)}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {group.issues.length}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={
                          group.unresolvedCount > 0 ? "secondary" : "default"
                        }
                      >
                        {group.unresolvedCount}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-2">
                        {group.unresolvedCount > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setBulkResolveGroup(group)}
                            className="gap-1"
                          >
                            <Layers className="w-3 h-3" />
                            Bulk Resolve ({group.unresolvedCount})
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {expanded && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/50">
                        <div className="p-4 space-y-2">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Individual issues ({group.issues.length})
                          </p>
                          <div className="space-y-1 max-h-60 overflow-y-auto">
                            {group.issues.map((issue) => {
                              const rowKey = String(issue.id);
                              return (
                                <div
                                  key={rowKey}
                                  className="flex items-center justify-between gap-4 py-2 px-3 bg-background rounded text-sm"
                                >
                                  <div className="flex-1 min-w-0">
                                    {issue.field_name && (
                                      <span className="text-muted-foreground font-medium mr-2">
                                        {issue.field_name}
                                      </span>
                                    )}
                                    <a
                                      href={issue.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:opacity-90 flex items-center gap-1"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {truncate(issue.url, 50)}
                                      <ExternalLink className="w-3 h-3 shrink-0" />
                                    </a>
                                    <span className="text-muted-foreground ml-2">
                                      {issue.status === "unresolved" ? (
                                        <Badge
                                          variant="secondary"
                                          className="text-xs"
                                        >
                                          Unresolved
                                        </Badge>
                                      ) : (
                                        <Badge
                                          variant="default"
                                          className="text-xs"
                                        >
                                          Resolved
                                        </Badge>
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {issue.status === "unresolved" && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => onResolve(issue)}
                                      >
                                        Resolve
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => onViewDetails(issue)}
                                    >
                                      Details
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => onScrapeUrl(issue.url)}
                                    >
                                      Scrape
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <BulkResolveDialog
        group={bulkResolveGroup}
        open={!!bulkResolveGroup}
        onOpenChange={(open) => !open && setBulkResolveGroup(null)}
      />
    </>
  );
}
